/**
 * Who is signed in, for the top-right corner only. Read from the Google ID token in the browser and kept in this
 * browser's localStorage: the server never receives or stores the name, email or picture (it uses only `sub`, see
 * public/privacy-policy.html). Display only, so the token is decoded, not verified (the server verifies it).
 */
import { readLocal, writeLocal } from "./storage";

export interface Profile {
  name: string;
  email?: string;
  picture?: string;
}

const KEY = "idphoto.profile.v1";

function base64UrlToText(part: string): string {
  const b64 = part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=");
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes); // UTF-8: Vietnamese names
}

/** Name, email and picture from a Google ID token; null when it is not a readable JWT. */
export function profileFromIdToken(token: string): Profile | null {
  try {
    const claims = JSON.parse(base64UrlToText(token.split(".")[1] ?? "")) as Record<string, unknown>;
    const text = (k: string) => (typeof claims[k] === "string" && claims[k] ? (claims[k] as string) : undefined);
    const email = text("email");
    const name = text("name") ?? text("given_name") ?? email;
    if (!name) return null;
    const picture = text("picture");
    return { name, email, picture: picture?.startsWith("https://") ? picture : undefined };
  } catch {
    return null;
  }
}

export function readProfile(): Profile | null {
  try {
    const p = JSON.parse(readLocal(KEY) ?? "null") as Profile | null;
    return p && typeof p.name === "string" ? p : null;
  } catch {
    return null;
  }
}

export function saveProfile(p: Profile | null): void {
  writeLocal(KEY, p ? JSON.stringify(p) : "");
}

/** Up to two initials for the avatar placeholder ("Nguyễn Văn An" → "NA"). */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const pick = words.length > 1 ? [words[0], words[words.length - 1]] : words;
  return pick.map((w) => w[0]!.toLocaleUpperCase("vi")).join("") || "?";
}
